import React, { useState, useEffect } from 'react';
import { FolderIcon, UsersIcon, ExternalLinkIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { API_URL, fetchWithAuth } from '../services/ApiService';

/**
 * SheetUsage component - shows which setlists contain this sheet
 * 
 * @param {Object} props - Component props
 * @param {string} props.sheetId - Sheet ID to check usage for
 * @param {string} props.sheetTitle - Sheet title for display
 */
const SheetUsage = ({ sheetId, sheetTitle }) => {
  const [setlists, setSetlists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('SheetUsage: useEffect triggered with sheetId:', sheetId, 'isExpanded:', isExpanded);
    if (!sheetId || !isExpanded) {
      console.log('SheetUsage: useEffect returning early - sheetId:', !!sheetId, 'isExpanded:', isExpanded);
      return;
    }

    const fetchSheetUsage = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('SheetUsage: Fetching usage for sheet ID:', sheetId);
        const result = await fetchWithAuth(`${API_URL}/sheets/${sheetId}/usage`);
        console.log('SheetUsage: API response:', result);
        if (result && result.data) {
          setSetlists(result.data.setlists || []);
          console.log('SheetUsage: Setlists found:', result.data.setlists?.length || 0);
        }
      } catch (error) {
        console.error('Error fetching sheet usage:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSheetUsage();
  }, [sheetId, isExpanded]);

  if (!sheetId) return null;

  return (
    <div className="mt-2">
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          console.log('SheetUsage: Button clicked, current expanded state:', isExpanded);
          console.log('SheetUsage: Sheet ID:', sheetId);
          setIsExpanded(!isExpanded);
        }}
        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
      >
        <FolderIcon className="w-3 h-3" />
        <span>Used in setlists</span>
        {isExpanded ? '▲' : '▼'}
      </button>

      {isExpanded && (
        <div className="mt-2 pl-4 border-l-2 border-gray-200">
          {loading ? (
            <p className="text-xs text-gray-500">Loading...</p>
          ) : error ? (
            <p className="text-xs text-red-500">Error: {error}</p>
          ) : setlists.length === 0 ? (
            <p className="text-xs text-gray-500">Not used in any setlists</p>
          ) : (
            <div className="space-y-1">
              {setlists.map(setlist => (
                <div key={setlist.id} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <FolderIcon className="w-3 h-3 text-gray-400" />
                    <Link 
                      to={`/setlist/${setlist.id}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {setlist.name}
                    </Link>
                    {setlist.band_name && (
                      <span className="flex items-center gap-1 text-gray-500">
                        <UsersIcon className="w-3 h-3" />
                        {setlist.band_name}
                      </span>
                    )}
                  </div>
                  <Link 
                    to={`/setlist/${setlist.id}`}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <ExternalLinkIcon className="w-3 h-3" />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SheetUsage;